import { useEffect, useState } from 'react';
import { formatBRL } from '../../utils/format';
import {
  DollarSign,
  ShoppingBag,
  TrendingUp,
  AlertTriangle,
  Download,
  Layers,
  ArrowUpRight,
} from 'lucide-react';

interface DashboardData {
  metrics: {
    paidOrdersCount: number;
    pendingOrdersCount: number;
    paidMerchandiseSalesCents: number;
    refundsTotalCents: number;
    merchandiseSalesAfterRefundsCents: number;
    averagePaidOrderValueCents: number;
    totalCollectedCents: number;
    shippingCollectedCents: number;
    currency: string;
  };
  top_products: {
    productId: string;
    title: string;
    productType: string;
    unitsSold: number;
    revenueCents: number;
  }[];
  low_stock: {
    variantId: string;
    productId: string;
    productTitle: string;
    variantTitle: string;
    sku: string;
    stockQuantity: number;
  }[];
}

interface AdminDashboardProps {
  onNavigate: (path: string) => void;
}

export function AdminDashboard({ onNavigate }: AdminDashboardProps) {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<'all' | '30d' | '7d'>('all');

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (dateRange === '7d') {
      const d = new Date();
      d.setDate(d.getDate() - 7);
      params.set('start_date', d.toISOString());
    } else if (dateRange === '30d') {
      const d = new Date();
      d.setDate(d.getDate() - 30);
      params.set('start_date', d.toISOString());
    }

    fetch(`/api/admin/metrics?${params.toString()}`)
      .then((res) => res.json())
      .then((d: any) => setData(d))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [dateRange]);

  const handleExportCsv = () => {
    const params = new URLSearchParams();
    if (dateRange === '7d') {
      const d = new Date();
      d.setDate(d.getDate() - 7);
      params.set('start_date', d.toISOString());
    } else if (dateRange === '30d') {
      const d = new Date();
      d.setDate(d.getDate() - 30);
      params.set('start_date', d.toISOString());
    }
    window.location.href = `/api/admin/metrics/export-csv?${params.toString()}`;
  };

  if (loading || !data) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-20 bg-surface rounded-xs" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 bg-surface rounded-xs" />
          ))}
        </div>
      </div>
    );
  }

  const { metrics, top_products, low_stock } = data;

  return (
    <div className="space-y-8">
      {/* Top Title & Filters Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border-subtle pb-6">
        <div>
          <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-primary">
            Métricas de Vendas Persistidas em D1
          </span>
          <h1 className="text-2xl sm:text-3xl font-serif font-normal text-ink mt-0.5">
            Painel Executivo de Vendas
          </h1>
        </div>

        <div className="flex items-center gap-3">
          {/* Date Range Selector */}
          <div className="flex bg-surface border border-border-subtle rounded-xs p-1 text-xs">
            <button
              onClick={() => setDateRange('7d')}
              className={`px-3 py-1 rounded-xs transition-colors ${
                dateRange === '7d' ? 'bg-ink text-ink-inverted font-medium' : 'text-ink-muted hover:text-ink'
              }`}
            >
              7 dias
            </button>
            <button
              onClick={() => setDateRange('30d')}
              className={`px-3 py-1 rounded-xs transition-colors ${
                dateRange === '30d' ? 'bg-ink text-ink-inverted font-medium' : 'text-ink-muted hover:text-ink'
              }`}
            >
              30 dias
            </button>
            <button
              onClick={() => setDateRange('all')}
              className={`px-3 py-1 rounded-xs transition-colors ${
                dateRange === 'all' ? 'bg-ink text-ink-inverted font-medium' : 'text-ink-muted hover:text-ink'
              }`}
            >
              Todo o Período
            </button>
          </div>

          <button
            onClick={handleExportCsv}
            className="bg-surface border border-border-subtle hover:border-ink/40 text-ink px-4 py-2 rounded-xs text-xs font-medium transition-colors flex items-center gap-1.5 shadow-subtle"
          >
            <Download className="w-3.5 h-3.5" /> Exportar CSV
          </button>
        </div>
      </div>

      {/* Primary KPI Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Metric 1: Paid Merchandise Sales */}
        <div className="bg-surface p-5 rounded-xs border border-border-subtle shadow-subtle space-y-2">
          <div className="flex items-center justify-between text-ink-muted text-xs">
            <span>Vendas Brutas (Mercadorias)</span>
            <DollarSign className="w-4 h-4 text-primary" />
          </div>
          <p className="font-mono text-2xl font-medium text-ink">
            {formatBRL(metrics.paidMerchandiseSalesCents)}
          </p>
          <p className="text-[11px] text-ink-muted">Exclui frete e taxas</p>
        </div>

        {/* Metric 2: Net Sales After Refunds */}
        <div className="bg-surface p-5 rounded-xs border border-border-subtle shadow-subtle space-y-2">
          <div className="flex items-center justify-between text-ink-muted text-xs">
            <span>Vendas Líquidas</span>
            <TrendingUp className="w-4 h-4 text-olive" />
          </div>
          <p className="font-mono text-2xl font-medium text-ink">
            {formatBRL(metrics.merchandiseSalesAfterRefundsCents)}
          </p>
          <p className="text-[11px] text-ink-muted">
            Após {formatBRL(metrics.refundsTotalCents)} em reembolsos
          </p>
        </div>

        {/* Metric 3: Average Order Value */}
        <div className="bg-surface p-5 rounded-xs border border-border-subtle shadow-subtle space-y-2">
          <div className="flex items-center justify-between text-ink-muted text-xs">
            <span>Ticket Médio (AOV)</span>
            <ShoppingBag className="w-4 h-4 text-primary" />
          </div>
          <p className="font-mono text-2xl font-medium text-ink">
            {formatBRL(metrics.averagePaidOrderValueCents)}
          </p>
          <p className="text-[11px] text-ink-muted">
            Baseado em {metrics.paidOrdersCount} pedidos pagos
          </p>
        </div>

        {/* Metric 4: Total Collected */}
        <div className="bg-surface p-5 rounded-xs border border-border-subtle shadow-subtle space-y-2">
          <div className="flex items-center justify-between text-ink-muted text-xs">
            <span>Total Arrecadado</span>
            <Layers className="w-4 h-4 text-ink-muted" />
          </div>
          <p className="font-mono text-2xl font-medium text-ink">
            {formatBRL(metrics.totalCollectedCents)}
          </p>
          <p className="text-[11px] text-ink-muted">
            Inclui {formatBRL(metrics.shippingCollectedCents)} de frete
          </p>
        </div>
      </div>

      {/* Orders Status Snapshot */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-surface p-4 rounded-xs border border-border-subtle flex items-center justify-between text-xs">
          <div>
            <p className="text-ink-muted">Pedidos Confirmados & Pagos</p>
            <p className="font-mono text-xl font-bold text-olive mt-0.5">{metrics.paidOrdersCount}</p>
          </div>
          <button
            onClick={() => onNavigate('/admin/pedidos?payment_status=paid')}
            className="text-xs text-primary font-medium hover:underline flex items-center gap-1"
          >
            Ver pedidos <ArrowUpRight className="w-3 h-3" />
          </button>
        </div>

        <div className="bg-surface p-4 rounded-xs border border-border-subtle flex items-center justify-between text-xs">
          <div>
            <p className="text-ink-muted">Aguardando Pagamento (Pendentes)</p>
            <p className="font-mono text-xl font-bold text-amber-700 mt-0.5">{metrics.pendingOrdersCount}</p>
          </div>
          <button
            onClick={() => onNavigate('/admin/pedidos?payment_status=pending')}
            className="text-xs text-primary font-medium hover:underline flex items-center gap-1"
          >
            Ver pendentes <ArrowUpRight className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Tables: Top Products & Low Stock Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Top Selling Products */}
        <div className="bg-surface p-6 rounded-xs border border-border-subtle shadow-subtle space-y-4">
          <div className="flex items-center justify-between border-b border-border-subtle pb-3">
            <h2 className="font-serif text-lg font-medium text-ink">Produtos Mais Vendidos</h2>
            <span className="text-[11px] text-ink-muted">Por receita bruta</span>
          </div>

          {top_products.length === 0 ? (
            <p className="text-xs text-ink-muted py-6 text-center italic">
              Nenhuma venda registrada no período selecionado.
            </p>
          ) : (
            <div className="divide-y divide-border-subtle text-xs">
              {top_products.map((prod, idx) => (
                <div key={prod.productId} className="py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="font-mono font-bold text-ink-muted w-4">{idx + 1}</span>
                    <div>
                      <p className="font-medium text-ink">{prod.title}</p>
                      <p className="text-[11px] text-ink-muted capitalize">Tipo: {prod.productType}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-mono font-medium text-ink">{formatBRL(prod.revenueCents)}</p>
                    <p className="text-[11px] text-ink-muted">{prod.unitsSold} unid.</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Low Stock Alerts */}
        <div className="bg-surface p-6 rounded-xs border border-border-subtle shadow-subtle space-y-4">
          <div className="flex items-center justify-between border-b border-border-subtle pb-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <h2 className="font-serif text-lg font-medium text-ink">Alerta de Estoque Baixo</h2>
            </div>
            <span className="text-[11px] text-ink-muted">&le; 5 unidades</span>
          </div>

          {low_stock.length === 0 ? (
            <p className="text-xs text-olive py-6 text-center font-medium">
              Todos os produtos com controle de estoque possuem níveis adequados.
            </p>
          ) : (
            <div className="divide-y divide-border-subtle text-xs">
              {low_stock.map((item) => (
                <div key={item.variantId} className="py-3 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-ink">{item.productTitle}</p>
                    <p className="text-[11px] text-ink-muted">{item.variantTitle} &bull; <span className="font-mono">{item.sku}</span></p>
                  </div>
                  <span className={`font-mono font-bold px-2.5 py-0.5 rounded-full ${
                    item.stockQuantity === 0 ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'
                  }`}>
                    {item.stockQuantity} un.
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
